import i18n from 'i18n'
import isEmptyObject from '@zatopek/core/src/utils/isEmptyObject'
import { SERVICES, TOPPINGS_SERVICE_TYPE, GA_FILTERS_SERVICE, GA_EVENT_NAMES, STEPS } from '../../utils/constants'
import { CONTRACT_STEPS } from 'services/stepper/constants'

// Util functions
const parseServicesTitle = (title, type) => {
  return `${i18n.t(TOPPINGS_SERVICE_TYPE[type])} - ${i18n.t(title)}`
}

const parseTitle = (title, type) => {
  return `${i18n.t(SERVICES[type])} - ${i18n.t(title)}`
}

const mapServices = (services) =>
  Object.keys(services).reduce((acc, value) => {
    const { title, type } = services[value]
    acc.push(parseServicesTitle(title, type))
    return acc
  }, [])

export const mapServicesNames = (services) =>
  Object.keys(services).reduce((acc, value) => {
    acc.push(services[value].name)
    return acc
  }, [])

const mapProducts = (product) => {
  const { title, energyType } = product
  return parseTitle(title, energyType)
}

export const mapProductsAndServices = ({ product, services }) => {
  const mappedServices = services ? mapServices(services).join('|') : ''
  const mappedProducts =
    product && services
      ? `${mapProducts(product)}|`
      : !services && product
      ? mapProducts(product)
      : i18n.t('common.contractOnlyMaintenance')

  const mappedProductsAndServices = `${mappedProducts}${mappedServices}`
  return mappedProductsAndServices
}

export const mapCheckoutServices = (services, step) =>
  Object.keys(services).reduce((acc, value) => {
    const {
      name,
      title,
      type,
      servicePrices: [{ priceAmount: price }],
      residential,
      pyme,
    } = services[value]

    const customSrc = residential && !pyme ? 'hogar' : !residential && pyme ? 'pyme' : residential && pyme ? 'hogar - pyme' : ''
    const category = `${customSrc} / servicio / ${name} / ${i18n.t(TOPPINGS_SERVICE_TYPE[type]).toLowerCase()} / ${i18n
      .t(title)
      .toLowerCase()}`
    const service = {
      name: i18n.t(title).toLowerCase(),
      price,
      quantity: 1,
      category,
      id: value,
      list: 'checkout',
    }

    if (!!step) {
      service.step = step + 1
    }
    acc.push(service)
    return acc
  }, [])

export const getCheckoutObjectRate = (rate, step) => {
  const { title, name, energyType, id, residential, pyme } = rate
  let price = 0
  let quantity = 1
  if (rate.subProducts) {
    price = rate.subProducts.reduce((a, v) => {
      if (v.calculatedCost) {
        return a + v.calculatedCost[v.energyType]
      } else {
        return a
      }
    }, 0)
    quantity = rate.subProducts.length
  } else if (rate.calculatedCost) {
    price = rate.calculatedCost[energyType]
  }
  const customSrc = residential && !pyme ? 'hogar' : !residential && pyme ? 'pyme' : residential && pyme ? 'hogar - pyme' : ''
  const category = `${customSrc} / producto / ${name} / ${i18n.t(SERVICES[energyType]).toLowerCase()} / ${i18n
    .t(title)
    .toLowerCase()}`

  const result = {
    name: i18n.t(title).toLowerCase(),
    price,
    quantity,
    category,
    id: id.split('/').pop(),
    list: 'checkout',
  }

  if (!!step) {
    result.step = step + 1
  }

  return result
}

/**
 * Gets the information for the ecommerce data of the event
 *
 * @param {Array} products - List of products or services
 * @param {Number | String} step
 * @returns {Object} - The ecommerce info
 */
export const getEcommerceData = (products, step, type) => {
  let actionStep = step
  // Sign step (-1) becomes 'validacion'
  if (actionStep !== 'compra') {
    actionStep = step < 0 ? 'validacion' : step + 1
  }

  const result = {
    [type]: {
      actionField: {
        step: actionStep,
        option: 'general',
      },
      products: products.map((product) => {
        // reference: https://developers.google.com/tag-manager/enhanced-ecommerce?hl=es#checkoutstep
        const { name, id, price, category, quantity } = product
        return {
          name,
          id, // transaction id if it is purchase or refund, else product/service id
          price,
          brand: 'Naturgy',
          category,
          quantity,
        }
      }),
    },
  }

  return result
}

/**
 * Get Common naturgy event
 *
 * @param {String} eventType - 'pageView', 'checkout', etc
 * @returns {Object} - Common naturgy event
 */
export const getCommonNaturgyEvent = (eventConfig) => {
  const { eventType: event } = eventConfig

  return {
    event,
    paginaVirtual: '',
    pais: 'espana',
    dominio: window.location.hostname,
    idioma: i18n.language,
    AreaNegocio: 'naturgy ib',
    publico: 'hogar', // Variables:  hogar, clientes, cur, homes
    seccion: 'area privada',
    categoria: 'contrata',
    producto: '', //
    tarifa: '', // Variables: tarifaeco, contrataenergia, tarifames, tarifas eco servigas
    negocio: 'residencial', // TODO: Por Ahora solo residencial cambiar a cuando se pueda recibir también negocio
    'User Type': '', // Variables: asistido, noasistido
    Assisted: '', // TODO solo se envia en canales de venta telefonica C2C (inbound) y CMB (outbound)
    mantenimiento: '',
    Origen: '', // Variables: oneclicktmes, sem1, sem, web
    tipo: '', // Variables: servicios, luz, gas, dual
    src: '', // Variables: hogar, residencial,
    sel: '',
    dist: 'luz', // TODO definir si se envia
    nnss: '', // Variables: lct  lcc  lmd  gct  gmd  lct  gct
    id: i18n.language,
    lgd: '', // Variables: 0, 1, 2
    cliente: '', // Variables: true, false
    spf: '', // Variables: true, false
    xs: false, // Variables: true, false
    'subcategoría del asistido': '', // call me back, click to call
  }
}

// Analytics
const datalayer = window.dataLayer

export const trackPage = (data) => {
  if (!datalayer || !datalayer.push) return

  // TODO Refine: https://redradix.atlassian.net/browse/NATS2-1289
  datalayer.push({
    ...getCommonNaturgyEvent({ eventType: 'pageView' }),
    ...data,
  })
}

export const trackEvent = (event, data) => {
  if (!datalayer || !datalayer.push) return
  const eventData = trackEvents[event](data)
  datalayer.push(eventData)
}

// Event Names

const {
  selectService,
  clickTariffDetail,
  changeCampaignClick,
  selectRate,
  nextStep,
  newContract,
  calculatorEvents,
  chooseAnotherRate,
  callMeBackButton,
  conditionsProductClick,
  checkoutEvent,
  signContractEvent,
  serviceDetails,
  clickServiceDetails,
  callMeBackClick,
  selectTopping,
  clickTopping,
  callMeBackClickPhone,
  clickLogin,
  sircaFail,
  callMeBackCallMe,
  loadThankYouPage,
} = GA_EVENT_NAMES

// Page tags
const translateService = (service) => GA_FILTERS_SERVICE[service]
const parseCampaignEventAction = (campaign) => `promocion_${campaign}`
const parseProductConditionEventAction = (product) => `condiciones_${product}`
const formattedAction = (eventAction) => `${eventAction}_proceso contratacion`
const trackEvents = {
  [selectService]: (service) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'energia',
    eventLabel: translateService(service),
  }),
  [clickTariffDetail]: ({ tariff, service }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `ver detalle_${i18n.t(`entity-${tariff}-title`)}`,
    eventLabel: translateService(service),
  }),
  [changeCampaignClick]: ({ service, campaign }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: parseCampaignEventAction(campaign),
    eventLabel: translateService(service),
  }),
  [selectRate]: (rate) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `contratacion_${i18n.t(rate.title)}`,
    eventLabel: translateService(rate.energyType),
  }),
  [chooseAnotherRate]: (text) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `ko_contratacion`,
    eventLabel: text,
  }),
  [callMeBackButton]: (text) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `call me back ko`,
    eventLabel: text,
  }),
  [conditionsProductClick]: ({ product, service }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: parseProductConditionEventAction(product),
    eventLabel: translateService(service),
  }),
  [nextStep]: ({ service, activeStep, product }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `siguiente step-${activeStep + 1}`,
    eventLabel: product ? `${product}_${translateService(service)}` : translateService(service),
  }),
  [newContract]: (channel) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'dar de alta',
    eventLabel: channel,
  }),
  [calculatorEvents]: ({ eventAction, eventLabel }) => ({
    event: 'event.calculadora',
    eventCategory: 'calculadora',
    eventAction: formattedAction(eventAction),
    eventLabel: eventLabel,
  }),
  [checkoutEvent]: (data) => ({
    event: 'event.ecommerce',
    ...data,
  }),
  [signContractEvent]: (data) => ({
    ...data,
  }),
  [callMeBackClick]: () => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'llamadme',
    eventLabel: 'contratacion_superior',
  }),
  [serviceDetails]: (data) => {
    const productStepIndex = CONTRACT_STEPS.findIndex((step) => step.id === STEPS.rate)
    const parsedService = mapCheckoutServices([data], productStepIndex)
    const ecommerce = getEcommerceData(parsedService, productStepIndex, 'detail')
    return {
      event: 'view-details',
      eventCategory: 'ecommerce',
      eventAction: 'product click',
      ecommerce,
    }
  },
  [clickServiceDetails]: ({ product, service }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `ver detalle_${product}`,
    eventLabel: service,
  }),
  [selectTopping]: ({ product, service }) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: `contratacion_${product}`,
    eventLabel: service,
  }),
  [clickTopping]: (topping) => {
    const result = {
      event: 'event.ecommerce',
    }

    if (!isEmptyObject(topping)) {
      const toppingStepIndex = CONTRACT_STEPS.findIndex((step) => step.id === STEPS.topping)
      const parsedService = mapCheckoutServices([topping], toppingStepIndex)
      result.ecommerce = getEcommerceData(parsedService, toppingStepIndex, 'click')
    }

    return result
  },
  [callMeBackClickPhone]: (phone) => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'llamanos',
    eventLabel: `ventas_${phone}`,
  }),
  [clickLogin]: () => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'inicia sesion',
    eventLabel: 'ventas - areaclientes',
  }),
  [sircaFail]: () => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'cancelar proceso contratacion',
    eventLabel: 'fallo scoring',
  }),
  [callMeBackCallMe]: () => ({
    event: 'event.naturgy',
    eventCategory: 'callToAction',
    eventAction: 'llamadme',
    eventLabel: 'cliente:ok-ventas',
  }),
  [loadThankYouPage]: (eventData) => ({
    event: 'purchase',
    ...eventData,
  }),
}
